import React, { useState, useEffect } from 'react';
import CgmModulePageLayout from './layout/CgmModulePageLayout';
import './CgmFilterToolbar.css';
import './CgmModulePage.css';
import EtatCgmModal from './EtatCgmModal';
import { getActiveCaisseYear, buildCaisseYearOptions } from '../utils/caisseActiveYear';

export default function AllEtatClientsGlobalPage() {
  const [showModal, setShowModal] = useState(false);
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);

  const [filterType, setFilterType] = useState('mois');
  const [jourMode, setJourMode] = useState('date');
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(() => getActiveCaisseYear());
  const years = buildCaisseYearOptions();

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredClients(clients);
    } else {
      const filtered = clients.filter((client) => {
        const searchTerm = searchQuery.toLowerCase();
        return (
          client.username?.toLowerCase().includes(searchTerm) ||
          client.nom?.toLowerCase().includes(searchTerm) ||
          (client.telephone || '').includes(searchTerm)
        );
      });
      setFilteredClients(filtered);
    }
  }, [searchQuery, clients]);

  const fetchClients = async () => {
    try {
      const response = await fetch('/api/clients');
      const data = await response.json();
      if (data.success) {
        setClients(data.clients || []);
        setFilteredClients(data.clients || []);
      }
    } catch (err) {
      console.error('Erreur lors du chargement des clients:', err);
    }
  };

  const handleShowEtat = () => {
    if (filterType === 'jour') {
      if (jourMode === 'date') {
        if (!selectedDate) {
          alert('Veuillez sélectionner une date');
          return;
        }
      } else if (!dateDebut || !dateFin) {
        alert('Veuillez sélectionner une période (début et fin)');
        return;
      } else if (new Date(dateDebut) > new Date(dateFin)) {
        alert('La date de début doit être antérieure à la date de fin');
        return;
      }
    }

    window.currentEtatClientId = null;
    window.currentEtatType = 'client';
    setSelectedClient(null);
    setShowModal(true);
  };

  const openForClient = (client) => {
    window.currentEtatClientId = client.id;
    window.currentEtatType = 'client';
    setSelectedClient(client);
    setShowModal(true);
  };

  const resolveClientFromSearch = () => {
    if (filteredClients.length === 0) return null;
    const q = searchQuery.trim().toLowerCase();
    if (!q) return null;
    const exactUsername = filteredClients.find(
      (c) => (c.username || '').toLowerCase() === q
    );
    if (exactUsername) return exactUsername;
    if (filteredClients.length === 1) return filteredClients[0];
    return filteredClients[0];
  };

  const showSelectedClientEtat = () => {
    const client = resolveClientFromSearch();
    if (client) {
      openForClient(client);
    } else {
      alert('Aucun client trouvé pour cette recherche');
    }
  };

  const canShowClientEtat = searchQuery.trim() !== '' && filteredClients.length > 0;

  const modalFilterType =
    filterType === 'jour' && jourMode === 'date'
      ? 'jour'
      : filterType === 'jour' && jourMode === 'periode'
        ? 'periode'
        : filterType === 'mois'
          ? 'mois'
          : filterType === 'annee'
            ? 'annee'
            : null;

  return (
    <CgmModulePageLayout
      crumb="Comptabilité clients · État"
      title="État clients"
      backTo="/"
      wide
    >
      <div className="cgm-list-toolbar">
        <div className="cgm-filter-panel cgm-period-panel">
          <div className="cgm-filter-panel__title">Rechercher un client</div>
          <div className="cgm-period-body">
            <div className="cgm-period-filter-row" style={{ width: '100%', maxWidth: 420 }}>
              <input
                type="text"
                className="form-control"
                placeholder="Filtrer par nom, username, téléphone…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canShowClientEtat) {
                    e.preventDefault();
                    showSelectedClientEtat();
                  }
                }}
              />
            </div>
            <div className="cgm-period-actions">
              <button
                type="button"
                className="cgm-toolbar-btn cgm-toolbar-btn--primary"
                onClick={showSelectedClientEtat}
                disabled={!canShowClientEtat}
              >
                Afficher l&apos;état
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="cgm-list-toolbar">
        <div className="cgm-filter-panel cgm-period-panel">
          <div className="cgm-filter-panel__title">Filtrer l&apos;état client</div>
          <div className="cgm-period-body">
            <div className="cgm-period-tabs">
              <button type="button" className={`cgm-period-tab ${filterType === 'jour' ? 'active' : ''}`} onClick={() => setFilterType('jour')}>Jour</button>
              <button type="button" className={`cgm-period-tab ${filterType === 'mois' ? 'active' : ''}`} onClick={() => setFilterType('mois')}>Mois</button>
              <button type="button" className={`cgm-period-tab ${filterType === 'annee' ? 'active' : ''}`} onClick={() => setFilterType('annee')}>Année</button>
            </div>

            {filterType === 'jour' && (
              <div className="cgm-period-jour-panel">
                <div className="cgm-period-mode-group" role="group" aria-label="Mode jour">
                  <div className="form-check">
                    <input className="form-check-input" type="radio" id="etatClientJourDate" name="etatClientJourMode" checked={jourMode === 'date'} onChange={() => setJourMode('date')} />
                    <label className="form-check-label" htmlFor="etatClientJourDate">Par date</label>
                  </div>
                  <div className="form-check">
                    <input className="form-check-input" type="radio" id="etatClientJourPeriode" name="etatClientJourMode" checked={jourMode === 'periode'} onChange={() => setJourMode('periode')} />
                    <label className="form-check-label" htmlFor="etatClientJourPeriode">Par période</label>
                  </div>
                </div>
                <div className="cgm-period-filter-row">
                  {jourMode === 'date' ? (
                    <>
                      <label className="mb-0">Date</label>
                      <input type="date" className="hono-input-date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
                    </>
                  ) : (
                    <>
                      <label className="mb-0">De</label>
                      <input type="date" className="hono-input-date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} />
                      <label className="mb-0">à</label>
                      <input type="date" className="hono-input-date" value={dateFin} onChange={(e) => setDateFin(e.target.value)} />
                    </>
                  )}
                </div>
              </div>
            )}

            {filterType === 'mois' && (
              <div className="cgm-period-filter-row">
                <label className="mb-0">Mois</label>
                <select className="hono-select-month" value={selectedMonth} onChange={(e) => setSelectedMonth(Number(e.target.value))}>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                  ))}
                </select>
                <label className="mb-0">Année</label>
                <select className="hono-select-year" value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
                  {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            )}

            {filterType === 'annee' && (
              <div className="cgm-period-filter-row">
                <label className="mb-0">Année</label>
                <select className="hono-select-year" value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
                  {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="cgm-period-actions">
              <button type="button" className="cgm-toolbar-btn cgm-toolbar-btn--primary" onClick={handleShowEtat}>
                Rechercher
              </button>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <EtatCgmModal
          show={showModal}
          onClose={() => {
            setShowModal(false);
            setSelectedClient(null);
          }}
          type="client"
          filterType={modalFilterType}
          date={filterType === 'jour' && jourMode === 'date' ? selectedDate : null}
          dateDebut={filterType === 'jour' && jourMode === 'periode' ? dateDebut : null}
          dateFin={filterType === 'jour' && jourMode === 'periode' ? dateFin : null}
          mois={filterType === 'mois' ? selectedMonth : null}
          annee={filterType === 'mois' || filterType === 'annee' ? selectedYear : null}
        />
      )}
    </CgmModulePageLayout>
  );
}
